// src/pages/warehouses/WarehouseForm.js

import React, { useState, useEffect } from 'react';
import useApi from '../../hooks/useApi';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErrorMessage from '../../components/common/ErrorMessage';
import CheckboxField from '../../components/CheckboxField';

const WarehouseForm = ({ warehouse, onClose, onSaveSuccess }) => {
  const api = useApi();
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    location: '',
    contactPerson: '',
    contactPhone: '',
    isDefault: false,
    isActive: true,
  });
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Populate form data if editing an existing warehouse
    if (warehouse) {
      setIsEditing(true);
      setFormData({
        name: warehouse.name || '',
        address: warehouse.address || '',
        location: warehouse.location || '',
        contactPerson: warehouse.contactPerson || '',
        contactPhone: warehouse.contactPhone || '',
        isDefault: warehouse.isDefault || false,
        isActive: warehouse.isActive !== undefined ? warehouse.isActive : true,
      });
    }
  }, [warehouse]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Basic validation
    if (!formData.name.trim()) {
      setError("Warehouse Name is required.");
      setLoading(false);
      return;
    }

    try {
      const payload = {
        name: formData.name,
        address: formData.address || '',
        location: formData.location || '',
        contactPerson: formData.contactPerson || '',
        contactPhone: formData.contactPhone || '',
        isDefault: formData.isDefault,
        isActive: formData.isActive,
      };

      if (isEditing) {
        await api.put(`warehouses/${warehouse._id}`, payload);
        alert("Warehouse updated successfully!");
      } else {
        await api.post('warehouses', payload);
        alert("Warehouse created successfully!");
      }
      onSaveSuccess(); // Notify parent (GenericListPage) to refresh data
      onClose(); // Close the modal
    } catch (err) {
      setError(err.message || "Operation failed.");
      console.error("Warehouse form submission error:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {loading && <LoadingSpinner />}
      {error && <ErrorMessage message={error} />}

      <div>
        <label htmlFor="name" className="block text-sm font-medium text-gray-700">
          Warehouse Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          name="name"
          id="name"
          value={formData.name}
          onChange={handleChange}
          required
          placeholder="e.g., Main Warehouse, Branch A"
          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-indigo-500 focus:border-indigo-500"
        />
      </div>

      <div>
        <label htmlFor="address" className="block text-sm font-medium text-gray-700">
          Address (Optional)
        </label>
        <textarea
          name="address"
          id="address"
          value={formData.address}
          onChange={handleChange}
          rows="2"
          placeholder="Street address of the warehouse"
          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-indigo-500 focus:border-indigo-500"
        ></textarea>
      </div>

      <div>
        <label htmlFor="location" className="block text-sm font-medium text-gray-700">
          Location (Optional)
        </label>
        <input
          type="text"
          name="location"
          id="location"
          value={formData.location}
          onChange={handleChange}
          placeholder="e.g., City, Region, or GPS coordinates"
          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-indigo-500 focus:border-indigo-500"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="contactPerson" className="block text-sm font-medium text-gray-700">
            Contact Person (Optional)
          </label>
          <input
            type="text"
            name="contactPerson"
            id="contactPerson"
            value={formData.contactPerson}
            onChange={handleChange}
            placeholder="Name of warehouse manager"
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-indigo-500 focus:border-indigo-500"
          />
        </div>

        <div>
          <label htmlFor="contactPhone" className="block text-sm font-medium text-gray-700">
            Contact Phone (Optional)
          </label>
          <input
            type="tel"
            name="contactPhone"
            id="contactPhone"
            value={formData.contactPhone}
            onChange={handleChange}
            placeholder="Phone number"
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-indigo-500 focus:border-indigo-500"
          />
        </div>
      </div>

      <div className="border p-4 rounded-md bg-gray-50 space-y-3">
        <legend className="text-sm font-semibold text-gray-700">Options</legend>
        
        <CheckboxField
          label="Set as Default Warehouse"
          id="isDefault"
          name="isDefault"
          checked={formData.isDefault}
          onChange={handleChange}
        />
        
        <CheckboxField
          label="Active"
          id="isActive"
          name="isActive"
          checked={formData.isActive}
          onChange={handleChange}
        />
      </div>

      <div className="flex justify-end space-x-3 mt-6">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading}
          className="px-4 py-2 border border-transparent rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-indigo-400"
        >
          {loading ? 'Saving...' : (isEditing ? 'Update Warehouse' : 'Create Warehouse')}
        </button>
      </div>
    </form>
  );
};

export default WarehouseForm;
